import { Dispatch } from 'react'
import { OnRequestChangeCallback } from '../services/APIs/BaseAPI'
import {
  AddKeywordsToPartsArgs,
  KeywordDto,
  KeywordGroupDto,
  MaterialsAndStandardsDto,
  MaterialSelectorDto,
  StandardKeywordDto,
  WorkingStepType,
} from '../services/APIs/InternalAPI/internal-api.contracts'
import { MaterialsAPI } from '../services/APIs/InternalAPI/MaterialsAPI'
import { ProjectsAPI } from './Projects/ProjectsAPI'
import { BaseController } from './BaseController'

// export type SelectableKeywords = Record<string, string[]>

export interface IMaterialsAPI {
  SaveKeyword: (
    keywords: StandardKeywordDto[]
  ) => Promise<MaterialsAndStandardsDto[]>
  GetMaterialsAsync: (
    filterTokens: string[],
    bomItemWorkingSteps?: WorkingStepType[]
  ) => Promise<MaterialSelectorDto>
  GetStandardsAndMaterials: () => Promise<MaterialsAndStandardsDto[]>
  AddKeywordsToParts: (
    projectId: string,
    args: AddKeywordsToPartsArgs
  ) => Promise<void>

  GetSelectableKeywordsForAssembly: (
    projectId: string,
    assemblyId: string,
    selectedKeywords: string[]
  ) => Promise<KeywordGroupDto>
}

export enum MaterialsOperations {
  GetMaterials = 'GetMaterials',
  GetStandardsAndMaterials = 'GetStandardsAndMaterials',
  SaveKeyword = 'SaveKeyword',
}

export class MaterialsController extends BaseController<IMaterialsAPI> {
  constructor(
    private organizationId: string,
    private dispatch?: Dispatch<unknown>,
    apiFactory?: (onRequestChange: OnRequestChangeCallback) => IMaterialsAPI
  ) {
    super(
      apiFactory
        ? apiFactory
        : (onRequestChanged) =>
            new MaterialsAPI(organizationId, onRequestChanged)
    )
  }

  private removeArticleCounter(token: string) {
    const parentesisIndex = token.lastIndexOf('(')

    return parentesisIndex > 0
      ? token.substring(0, parentesisIndex).trim()
      : token
  }

  public async GetStandardsAndMaterials() {
    const resp = await this.api.GetStandardsAndMaterials()

    return resp
  }

  public async SetKeywordVisible(
    keyword: StandardKeywordDto,
    checked: boolean
  ) {
    const resp = await this.api.SaveKeyword([
      { ...keyword, isSelected: checked },
    ])

    return resp
  }

  public async GetMaterialsAsync(
    tokens: string[],
    groupOrderFilter?: string,
    projectId?: string,
    isPurchasingPart?: boolean,
    bomItemWorkingSteps?: WorkingStepType[],
    showGroups?: string[]
  ) {
    const tokensWithoutCounters = tokens.map(this.removeArticleCounter)

    let resp: Partial<MaterialSelectorDto>

    if (!projectId) {
      resp = await this.api.GetMaterialsAsync(tokensWithoutCounters)
    } else {
      resp = await new ProjectsAPI(
        this.organizationId
      ).GetMaterialTokensInProjectContext(
        projectId,
        tokensWithoutCounters,
        isPurchasingPart,
        bomItemWorkingSteps,
        this.onRequestChange
      )
    }

    if (showGroups) {
      Object.keys(resp.selectableTokens).forEach((group) => {
        if (!showGroups.includes(group)) {
          delete resp.selectableTokens[group]
        }
      })

      return resp
    }

    if (groupOrderFilter) {
      return Object.assign(resp, {
        selectableTokens: {
          [groupOrderFilter]: resp.selectableTokens[groupOrderFilter],
        },
      })
    }

    return resp
  }

  public async AddKeywordsToRows(
    projectId: string,
    args: {
      keywords: KeywordDto[]
      rowIds: string[]
    }
  ) {
    try {
      // setTimeout(() => {
      //   batch(() => {
      //     args.rowIds.forEach((rowId) => {
      //       this.dispatch(
      //         newProjectActions.setBomItemProperties({
      //           id: rowId,
      //           operationPending: true,
      //         })
      //       )
      //     })
      //   })
      // }, 60)

      // this.dispatch(newProjectActions.removePrices())

      await this.api.AddKeywordsToParts(projectId, {
        keywords: args.keywords.map((x) => x.originalKeyword),
        rowIds: args.rowIds,
      })
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  // private debouncedGetSelectableKeywordsForAssembly = _.debounce(async (projectId, assemblyId, selectedKeywords) => await this.api.GetSelectableKeywordsForAssembly(projectId, assemblyId, selectedKeywords), 2000, { leading: true, trailing: false })

  public async GetSelectableKeywordsForAssembly(
    projectId: string,
    assemblyId: string,
    selectedKeywords: string[]
  ): Promise<Record<string, KeywordDto[]>> {
    try {
      const resp = await this.api.GetSelectableKeywordsForAssembly(
        projectId,
        assemblyId,
        selectedKeywords
      )

      // to keep compatibility, will be renamed to keywordGroups later
      return resp.keywordGroups
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  // private sortMaterialGroups(keywordGroups: { [group: string]: KeywordDto[] }) {
  //   const dimensionsGroup = keywordGroups['dimensions']
  //   if (dimensionsGroup) {
  //     const collator = new Intl.Collator(undefined, {
  //       numeric: true,
  //       sensitivity: 'base',
  //     })
  //     keywordGroups['dimensions'] = dimensionsGroup
  //       .sort(collator.compare) // sort by numbers
  //       .sort((a, b) => {
  //         // sort by measure (thick, outer, weight, etc)
  //         const measureA = a.split(' ').pop()
  //         const measureB = b.split(' ').pop()

  //         return measureA < measureB ? -1 : 1
  //       })
  //   }
  // }
}
